/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 에디터의 취소(undo)/복원(redo) 히스토리를 정의한다.
 *
 * @file /src/rootin/core/History.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 9. 3.
 */
import { Editor } from '@rootin/core/Editor';
import { Element } from '@rootin/base/Element';
import { Html } from '@utils/Html';

/**
 * 히스토리 엔트리
 */
export type HistoryData =
    | { type: 'edit'; id: string; before: any; after: any; beforeCaret: any; afterCaret: any }
    | { type: 'batch'; entries: HistoryData[] };

export class History {
    /**
     * 히스토리 스택
     */
    stack: HistoryData[] = [];

    /**
     * 재실행 스택
     */
    redos: HistoryData[] = [];

    /**
     * 엘리먼트별 마지막 상태기록
     */
    snapshots: Map<string, any> = new Map();

    /**
     * 취소/복원중인지 여부
     */
    applying: boolean = false;

    /**
     * 에디터의 취소(undo)/복원(redo) 히스토리를 정의한다.
     *
     * @param editor - 히스토리를 사용할 에디터
     */
    constructor(public editor: Editor) {}

    /**
     * 새 기록을 스택에 추가하고 redo 스택을 비운다.
     *
     * @param data - 쌓을 엔트리
     */
    push(data: HistoryData): void {
        this.stack.push(data);
        this.redos = [];
    }

    /**
     * 엘리먼트의 변경내용을 기록한다.
     *
     * @param element - 변경된 엘리먼트
     * @param beforeCaret - 편집이전 캐럿위치
     */
    recordEdit(element: Element, beforeCaret: any): void {
        const id = element.getId();
        const after = element.getValue();
        const afterCaret = element.captureCaret();

        const last = this.stack[this.stack.length - 1];
        if (last?.type === 'edit' && last.id === id && last.beforeCaret === beforeCaret) {
            last.after = after;
            last.afterCaret = afterCaret;
        } else {
            const before = this.snapshots.get(id) ?? null;
            this.push({ type: 'edit', id, before, after, beforeCaret, afterCaret });
        }

        this.snapshots.set(id, after);
    }

    /**
     * 실행취소를 한다.
     */
    async undo(): Promise<void> {
        const data = this.stack.pop();
        if (data === undefined) {
            return;
        }

        this.redos.push(data);

        this.applying = true;
        await this.apply(data, true);
        this.applying = false;

        this.editor.onChange();
    }

    /**
     * 실행취소를 복원한다.
     */
    async redo(): Promise<void> {
        const data = this.redos.pop();
        if (data === undefined) {
            return;
        }

        this.stack.push(data);

        this.applying = true;
        await this.apply(data, false);
        this.applying = false;

        this.editor.onChange();
    }

    /**
     * 특정 상태로 적용한다.
     *
     * @param data - 적용할 히스토리 상태
     * @param is_undo - undo 방향이면 true, redo 방향이면 false
     */
    async apply(data: HistoryData, is_undo: boolean): Promise<void> {
        if (data.type === 'batch') {
            const entries = is_undo ? [...data.entries].reverse() : data.entries;
            for (const entry of entries) {
                await this.apply(entry, is_undo);
            }
            return;
        }

        const $element = Html.get(`[data-element="${data.id}"]`, this.editor.$getContainer());
        const element = $element?.getData('element') as Element | undefined;
        if (element === undefined) {
            return;
        }

        const value = is_undo ? data.before : data.after;
        const caret = is_undo ? data.beforeCaret : data.afterCaret;

        await element.setValue(value);
        element.restoreCaret(caret);

        this.snapshots.set(data.id, value);
    }

    /**
     * 히스토리 전체 기록을 초기화한다.
     */
    clear(): void {
        this.stack = [];
        this.redos = [];
        this.snapshots.clear();
    }

    /**
     * 지금 DOM에 있는 모든 엘리먼트(컨테이너 포함)의 현재값을 스냅샷으로 미리 채워둔다.
     * 이걸 안 해두면 `recordEdit()`의 `before`가 `snapshots.get(id) ?? null`이라, 세션 중
     * 그 엘리먼트에 대한 첫 편집을 기록할 때 아직 스냅샷이 없어 `before`가 그냥 `null`이
     * 되고, undo해도 "로드 당시 원본으로 복원"이 아니라 빈 값으로 떨어지는 문제가 있었다
     * (실측 확인). `Editor.setValue()`로 새 콘텐츠가 로드된 직후 호출한다.
     */
    seedSnapshots(): void {
        Html.all('rootin-element', this.editor.root.body.$getElement()).forEach(($el) => {
            const element = $el.getData('element') as Element | null;
            if (element !== null) {
                this.snapshots.set(element.getId(), element.getValue());
            }
        });
    }
}
